import React, { useState } from 'react'; // Added useState
import { UnitType } from '../../utils/mockData';
import PlanViewerModal from '../common/PlanViewerModal'; // Corrected import path
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Stack,
  Divider,
  useTheme,
  Tooltip,
  IconButton,
  Link as MuiLink,
} from '@mui/material';
import {
  BedOutlined,
  BathtubOutlined,
  CropSquareOutlined as TwoDIcon, // Using CropSquare for 2D icon
  ViewInArOutlined as ThreeDIcon, // Using ViewInAr for 3D icon (outlined version)
  SquareFootOutlined,
  AttachMoney, // For Price
  CheckCircleOutline, // For Available
  CancelOutlined, // For Sold
  PauseCircleOutline, // For Reserved
  DescriptionOutlined, // For unit description if needed as an icon
} from '@mui/icons-material';

type UnitAvailabilityCardProps = {
  unit: UnitType;
};

const UnitAvailabilityCard: React.FC<UnitAvailabilityCardProps> = ({ unit }) => {
  const theme = useTheme();

  // State for PlanViewerModal
  const [unitModalOpen, setUnitModalOpen] = useState(false);
  const [unitSelectedImageUrl, setUnitSelectedImageUrl] = useState('');
  const [unitSelectedImageTitle, setUnitSelectedImageTitle] = useState('');
  const [unitSelectedImageDescription, setUnitSelectedImageDescription] = useState('');

  const handleOpenUnitModal = (imageUrl: string, title: string, description?: string) => {
    setUnitSelectedImageUrl(imageUrl);
    setUnitSelectedImageTitle(title);
    setUnitSelectedImageDescription(description || '');
    setUnitModalOpen(true);
  };

  const handleCloseUnitModal = () => {
    setUnitModalOpen(false);
  };

  const getAvailabilityChip = (availability: string) => {
    const lowerAvailability = availability.toLowerCase();
    let color: "success" | "error" | "warning" | "info" = "info";
    let icon = <PauseCircleOutline />;

    if (lowerAvailability === 'available') {
      color = 'success';
      icon = <CheckCircleOutline />;
    } else if (lowerAvailability === 'sold') {
      color = 'error';
      icon = <CancelOutlined />;
    } else if (lowerAvailability === 'reserved') {
      color = 'warning';
      icon = <PauseCircleOutline />;
    }

    return (
      <Chip
        label={unit.availability}
        color={color}
        icon={icon}
        size="small"
        sx={{
          textTransform: 'capitalize',
          fontWeight: 'bold',
        }}
      />
    );
  };

  const formattedPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(unit.price);

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" component="h3" fontWeight={600} noWrap>
            {unit.unitName}
          </Typography>
          {getAvailabilityChip(unit.availability)}
        </Box>

        <Divider sx={{ my: 1 }} />

        <Stack spacing={1.5} sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
            <AttachMoney fontSize="small" sx={{ mr: 1 }} color="primary"/>
            <Typography variant="body1" fontWeight="bold" color="primary.main">
              {formattedPrice}
            </Typography>
          </Box>

          <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
              <BedOutlined fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2">{unit.bedrooms} Bed</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
              <BathtubOutlined fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2">{unit.bathrooms} Bath</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
              <SquareFootOutlined fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="body2">{unit.area} sq ft</Typography>
            </Box>
          </Stack>

          {/* Unit Description */}
          {unit.unitDescription && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{
                display: '-webkit-box',
                WebkitLineClamp: 3, // Show up to 3 lines
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minHeight: '3.6em' // Approximate height for 3 lines to avoid layout shift
              }}>
                {unit.unitDescription}
              </Typography>
            </Box>
          )}

          {/* Floor Plans */}
          {unit.floorPlans && unit.floorPlans.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Floor Plans:
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                {unit.floorPlans.map((plan, index) => (
                  <Tooltip title={plan.description || `${plan.type} Floor Plan`} key={index}>
                    <Chip
                      icon={plan.type === '2D' ? <TwoDIcon /> : <ThreeDIcon />}
                      label={plan.type}
                      variant="outlined"
                      size="small"
                      clickable
                      onClick={() => handleOpenUnitModal(plan.url, `${unit.unitName} - ${plan.type} Plan`, plan.description)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Tooltip>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </CardContent>
      <PlanViewerModal
        open={unitModalOpen}
        onClose={handleCloseUnitModal}
        imageUrl={unitSelectedImageUrl}
        title={unitSelectedImageTitle}
        description={unitSelectedImageDescription}
      />
    </Card>
  );
};

export default UnitAvailabilityCard;
